import { Component, inject, OnInit, signal, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ContainerComponent } from '../../../shared/container/container.component';
import { MaterialModule } from '../../../shared/material/material.module';
import { FormBuilder, Validators, FormArray } from '@angular/forms';
import { UserPreferencesCacheService } from '../../../shared/services/user-preferences-cache.service';
import {
  Council,
  CouncilControllerService,
  Address,
  ProfessionControllerService,
  Profession,
  ServiceDemandControllerService,
  ProfileProviderPjControllerService,
  ProfileProviderPJ,
  ServiceDemand,
} from '../../../core/generated';
import { AvailabilityComponent } from '../../../shared/availability/availability.component';
import { CustomDate } from '../../../shared/services/custom-date.service';
import { AuthCustomService } from '../../../security/auth-custom.service';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { CurrencyMaskDirective } from '../../../shared/directives/currency-mask.directive';
import { ProviderPJContextService } from '../../../shared/services/provider-pj-context.service';
import { getEnumKey } from '../../../shared/utils/enum.util';

// Register pt-BR locale for currency formatting
registerLocaleData(localePt);

@Component({
    selector: 'app-service-demand-form',
    standalone: true,
    imports: [
        CommonModule,
        MaterialModule,
        AvailabilityComponent,
        CurrencyMaskDirective,
        ContainerComponent
    ],
    templateUrl: './service-demand-form.component.html',
    styleUrls: ['./service-demand-form.component.scss']
})
export class ServiceDemandFormComponent
  implements OnInit, OnDestroy {
  serviceDemandService = inject(ServiceDemandControllerService);
  deviceService = inject(DeviceDetectorService);
  UserPreferencesCacheService = inject(UserPreferencesCacheService);
  councilService = inject(CouncilControllerService);
  professionsService = inject(ProfessionControllerService);
  router: Router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  fb = inject(FormBuilder);
  customDate = inject(CustomDate);
  profileProviderPJService = inject(ProfileProviderPjControllerService);
  authCustomService = inject(AuthCustomService);
  providerPJContextService = inject(ProviderPJContextService);
  serviceDemand: ServiceDemand[] = [];
  isMobile = this.deviceService.isMobile();
  readonly panelOpenState = signal(false);
  councils: Council[] = [];
  providerPJId: string | null = null;
  id: string | null = null;
  addresses: Address[] = [];
  providerPj!: ProfileProviderPJ;
  private subscriptions: any[] = [];

  formServiceDemand = this.fb.group({
    id: [''],
    cnpj: [''],
    professionName: ['', Validators.required],
    specialty: ['', Validators.required],
    activityAreas: this.fb.array([]),
    adultCare: [false],
    pediatricCare: [false],
    serviceFormatOnLine: [false],
    serviceFormatPresential: [false],
    address: [null as Address | null],
    availability: this.fb.array([]),
    publicationDate: [''],
    expirationPublicationDate: [''],
    amount: [null as number | null],
    billingUnit: ['HOUR'],
    description: [''],
    status: ['DRAFT'],
    emergencyService: [false],
    emergencyServiceAvailability: this.fb.array([]),
  });

  professionsOptions: Profession[] = [];
  isLoading = false;

  constructor() { }

  ngOnInit() {
    // Initialize FormArrays

    const days = [21, 22, 23, 31, 32, 33, 41, 42, 43, 51, 52, 53, 61, 62, 63, 71, 72, 73, 81, 82, 83];

    const availabilityArray = this.formServiceDemand.get(
      'availability'
    ) as FormArray;
    availabilityArray.clear();

    days.forEach((availability) => {
      availabilityArray.push(this.fb.control(availability));
    });

    const emergencyServiceAvailabilityArray = this.formServiceDemand.get(
      'emergencyServiceAvailability'
    ) as FormArray;
    emergencyServiceAvailabilityArray.clear();

    days.forEach((availability) => {
      emergencyServiceAvailabilityArray.push(this.fb.control(availability));
    });


    
    // Get Id from route
    this.providerPJId =
      this.route.parent?.snapshot.paramMap.get('id') ??
      this.route.snapshot.paramMap.get('id') ??
      null;

    // Set CNPJ if we already have  data
    if (this.providerPj) {
      this.formServiceDemand.get('cnpj')?.setValue(this.providerPj.cnpj || null);
    }

    // Subscribe to context changes
    const subId = this.providerPJContextService.providerId$.subscribe((id) => {
      if (id !== null) this.providerPJId = id;
    });
    const subProfilePj = this.providerPJContextService.providerPJ$.subscribe(
      (c) => {
        if (c !== null && c !== this.providerPj) {
          this.providerPj = c;
          this.formServiceDemand.get('cnpj')?.setValue(c.cnpj || null);
        }

      }
    );
    this.subscriptions.push(subId, subProfilePj);

    const routeSnapshot = this.route.snapshot;
    if (routeSnapshot.paramMap.has('id')) {
      const idParam = routeSnapshot.paramMap.get('id');
      this.id = idParam ? idParam : null;
      if (this.id !== null) {
        this.loadServiceDemand(this.id);
      }
    }

    this.loadCouncils();
    if (this.providerPJId) {
      this.loadAddresses(this.providerPJId).then(() => {
        if (this.id) {
          this.loadServiceDemand(this.id);
        }
      });
    }
  }

  async loadAddresses(profileId: string): Promise<void> {
    const Id = parseInt(profileId, 10);
    return new Promise((resolve) => {
      const sub = this.profileProviderPJService.getAllAddresses
        (Id)
        .subscribe((addresses) => {
          this.addresses = addresses;
          resolve();
        });
      this.subscriptions.push(sub);
    });
  }

  getIdFromRoute(): string | null {
    let routeSnapshot = this.route.snapshot;

    // Verificar na rota atual
    if (routeSnapshot.paramMap.has('id')) {
      return routeSnapshot.paramMap.get('id');
    }

    // Verificar na rota pai
    if (routeSnapshot.parent && routeSnapshot.parent.paramMap.has('id')) {
      return routeSnapshot.parent.paramMap.get('id');
    }

    // Verificar na rota avô
    if (
      routeSnapshot.parent &&
      routeSnapshot.parent.parent &&
      routeSnapshot.parent.parent.paramMap.has('id')
    ) {
      return routeSnapshot.parent.parent.paramMap.get('id');
    }

    // Se não encontrou nada, tenta extrair da URL atual
    const urlMatch = this.router.url.match(/\/providerPJ\/([^\/]+)/);
    if (urlMatch) {
      return urlMatch[1];
    }

    return null;
  }

  loadServiceDemand(id: string) {
    this.isLoading = true;
    const sub = this.serviceDemandService
      .findById3(Number(id))
      .subscribe((serviceDemand) => {

        const serviceAddress = serviceDemand.address;

        // Patch all form values including checkboxes, dates, and other fields
        this.formServiceDemand.patchValue({
          id: serviceDemand.id?.toString() || '',
          cnpj: serviceDemand.cnpj,
          professionName: serviceDemand.professionName,
          specialty: serviceDemand.specialty,
          adultCare: serviceDemand.adultCare ?? false,
          pediatricCare: serviceDemand.pediatricCare ?? false,
          serviceFormatOnLine: serviceDemand.serviceFormatOnLine ?? false,
          serviceFormatPresential: serviceDemand.serviceFormatPresential ?? false,
          publicationDate: serviceDemand.publicationDate,
          expirationPublicationDate: serviceDemand.expirationPublicationDate,
          amount: serviceDemand.amount,
          billingUnit: serviceDemand.billingUnit ?? 'HOUR',
          description: serviceDemand.description,
          status: serviceDemand.status ?? ServiceDemand.StatusEnum.Draft,
          emergencyService: serviceDemand.emergencyService ?? false,
        });

        if (serviceAddress) {
          this.setAddressInForm(serviceAddress);
        }

        // Atualize manualmente o FormArray de availability
        const availabilityArray = this.formServiceDemand.get(
          'availability'
        ) as FormArray;
        availabilityArray.clear();
        if (serviceDemand.availability) {
          serviceDemand.availability.forEach((availability) => {
            availabilityArray.push(this.fb.control(availability));
          });
        }

        // Atualize manualmente o FormArray de emergencyServiceAvailability
        const emergencyServiceAvailabilityArray = this.formServiceDemand.get(
          'emergencyServiceAvailability'
        ) as FormArray;
        emergencyServiceAvailabilityArray.clear();
        if (serviceDemand.emergencyServiceAvailability) {
          serviceDemand.emergencyServiceAvailability.forEach(
            (availability) => {
              emergencyServiceAvailabilityArray.push(
                this.fb.control(availability)
              );
            }
          );
        }

        // Carrega as áreas de atuação após carregar os dados
        setTimeout(() => {
          this.loadActivityAreasFromServiceDemand(serviceDemand.activityAreas || []);
          this.isLoading = false;
        }, 500);
      });
    this.subscriptions.push(sub);
  }

  // Método para carregar áreas de atuação selecionadas ao editar
  private loadActivityAreasFromServiceDemand(selectedAreas: string[]) {
    this.initializeActivityAreasCheckboxes();

    const availableAreas = this.getActivityArea();
    const activityAreasArray = this.activityAreas;

    selectedAreas.forEach(selectedArea => {
      const index = availableAreas.indexOf(selectedArea);
      if (index >= 0 && activityAreasArray.at(index)) {
        activityAreasArray.at(index).setValue(true);
      }
    });
  }

  private setAddressInForm(serviceAddress: Address): void {
    const matchedAddress = this.addresses.find(
      (addr) => addr.id === serviceAddress.id
    );
    if (matchedAddress) {
      this.formServiceDemand.get('address')?.setValue(matchedAddress);
    } else {
      this.addresses.push(serviceAddress);
      this.formServiceDemand.get('address')?.setValue(serviceAddress);
    }
    this.formServiceDemand.get('serviceFormatPresential')?.setValue(true);
  }

  async loadCouncils() {
    this.isLoading = true;
    const sub = this.professionsService
      .findAll3()
      .subscribe({
        next: (professions) => {
          this.professionsOptions = professions;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading professions:', error);
          this.isLoading = false;
        }
      });

    this.subscriptions.push(sub);
  }

  async addServiceDemand() {
    // Check form validity first
    if (this.formServiceDemand.invalid) {
      this.formServiceDemand.markAllAsTouched();
      return;
    }

    const value = this.formServiceDemand.value;

    // Regra especial para status: se publicationDate estiver nulo/vazio, DRAFT, caso contrário OPEN
    const statusValue = (!value.publicationDate || value.publicationDate === '') 
      ? ServiceDemand.StatusEnum.Draft
      : ServiceDemand.StatusEnum.Open;

    const serviceDemand: ServiceDemand = {
      id: value.id ? Number(value.id) : undefined,
      cnpj: this.providerPj?.cnpj ?? value.cnpj ?? '',
      professionName: value.professionName ?? '',
      specialty: value.specialty ?? '',
      activityAreas: this.preparTosaveActivityAreas(),
      adultCare: value.adultCare ?? false,
      pediatricCare: value.pediatricCare ?? false,
      serviceFormatOnLine: value.serviceFormatOnLine ?? false,
      serviceFormatPresential: value.serviceFormatPresential ?? false,
      address: value.address ?? undefined,
      availability: (value.availability as number[]) ?? [],
      publicationDate: value.publicationDate ?? '',
      expirationPublicationDate: value.expirationPublicationDate ?? '',
      amount: value.amount ?? undefined,
      billingUnit: value.billingUnit ?? 'HOUR',
      description: value.description ?? '',
      status: statusValue,
      emergencyService: value.emergencyService ?? false,
      emergencyServiceAvailability:
        (value.emergencyServiceAvailability as number[]) ?? [],
    };

    let sub;
    if (this.id) {
      sub = this.serviceDemandService
        .update2(Number(this.id), serviceDemand)
        .subscribe({
          next: () => {
            this.router.navigate([
              '/providerPJ',
              this.providerPJId,
              'serviceDemand',
            ]);
          }
        });
    } else {
      sub = this.serviceDemandService
        .save2(serviceDemand)
        .subscribe({
          next: () => {
            this.router.navigate([
              '/providerPJ',
              this.providerPJId,
              'serviceDemand',
            ]);
          }
        });
    }
    this.subscriptions.push(sub);
  }

  getProfessionName() {
    if (this.professionsOptions) {
      const tiposUnicos = [
        ...new Set(
          this.professionsOptions.map(
            (item) => item.professionName
          )
        ),
      ];
      return tiposUnicos.filter(name => name !== undefined);
    }
    return [];
  }

  getSpecialty() {
    if (
      this.formServiceDemand.controls['professionName'].value &&
      this.professionsOptions
    ) {
      const professionName =
        this.formServiceDemand.controls['professionName']?.value;
      if (professionName && this.professionsOptions) {
        const professionFilter = this.professionsOptions.filter(
          (profession) =>
            profession.professionName === professionName
        );
        const tiposUnicos = [
          ...new Set(
            professionFilter
              .map((item) => item.specialty)
              .filter((s) => !!s)
          ),
        ];
        return tiposUnicos;
      }
    }
    return [];
  }

  getActivityArea() {
    if (
      this.formServiceDemand.controls['specialty'].value &&
      this.professionsOptions
    ) {
      const professionName = this.formServiceDemand.controls['professionName']?.value;
      const specialty = this.formServiceDemand.controls['specialty']?.value;

      const professionFilter = this.professionsOptions.filter(
        (profession) =>
          profession.professionName === professionName &&
          profession.specialty === specialty
      );

      const activityAreas = professionFilter
        .map((item) => item.activityArea)
        .filter((area) => area !== undefined && area !== '');

      return activityAreas;
    }
    return [];
  }

  preparTosaveActivityAreas(): string[] {
    return this.getSelectedActivityAreas();
  }

  get activityAreas() {
    return this.formServiceDemand.get('activityAreas') as FormArray;
  }

  // Inicializa os checkboxes das áreas de atuação baseado nas áreas disponíveis
  initializeActivityAreasCheckboxes() {
    const activityAreasArray = this.activityAreas;
    activityAreasArray.clear();

    const availableAreas = this.getActivityArea();
    availableAreas.forEach(() => {
      activityAreasArray.push(this.fb.control(false));
    });
  }

  // Chamado quando uma área de atuação é selecionada/desmarcada
  onActivityAreaChange(index: number, isChecked: boolean) {
    const activityAreasArray = this.activityAreas;
    activityAreasArray.at(index).setValue(isChecked);
  }

  // Retorna as áreas selecionadas como array de strings
  getSelectedActivityAreas(): string[] {
    const availableAreas = this.getActivityArea();
    const selectedAreas: string[] = [];

    this.activityAreas.controls.forEach((control, index) => {
      if (control.value && availableAreas[index]) {
        selectedAreas.push(availableAreas[index]);
      }
    });

    return selectedAreas;
  }

  // Método chamado quando a especialidade muda para reinicializar os checkboxes
  onSpecialtyChange() {
    // Limpa imediatamente os checkboxes atuais
    this.activityAreas.clear();

    // Aguarda um ciclo de detecção de mudanças antes de reinicializar
    setTimeout(() => {
      this.initializeActivityAreasCheckboxes();
    }, 50);
  }

  // Método chamado quando a profissão muda para limpar especialidade e áreas
  onProfessionChange() {
    // Limpa a especialidade e áreas quando a profissão muda
    this.formServiceDemand.get('specialty')?.setValue('');
    this.activityAreas.clear();
  }

  get availability() {
    return this.formServiceDemand.get('availability') as FormArray;
  }

  get emergencyServiceAvailability() {
    return this.formServiceDemand.get(
      'emergencyServiceAvailability'
    ) as FormArray;
  }

  onAvailabilityChange(newAvailability: FormArray) {
    this.formServiceDemand.setControl('availability', newAvailability);
  }

  onEmergencyAvailabilityChange(newAvailability: FormArray) {
    this.formServiceDemand.setControl(
      'emergencyServiceAvailability',
      newAvailability
    );
  }

  addActivityArea() {
    this.activityAreas.push(
      this.fb.group({
        activityArea: [''],
        activityAreaRQE: [''],
      })
    );
  }

  removeActivityArea(index: number) {
    this.activityAreas.removeAt(index);
  }

  dateFilter = (date: Date | null): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set the time to midnight to compare only dates
    const result = date ? date >= today : false;
    return result;
  };

  dateLimitFilter = (date: Date | null): boolean => {
    let publishDate = new Date();
    let today = new Date();

    if (this.formServiceDemand.controls.publicationDate.value) {
      publishDate = new Date(
        this.formServiceDemand.controls.publicationDate.value
      );
    } else {
      this.formServiceDemand.controls.expirationPublicationDate.reset();
    }

    if (this.formServiceDemand.controls.publicationDate.value) {
      today = new Date(this.formServiceDemand.controls.publicationDate.value);
    }
    today.setHours(0, 0, 0, 0); // Set the time to midnight to compare only dates
    const result = date ? date >= today : false;
    return result;
  };

  ngOnDestroy() {
    this.subscriptions.forEach(
      (sub) => sub && sub.unsubscribe && sub.unsubscribe()
    );
  }
}
